import { SourceDocumentRegion } from "core/types";
import { RefObject, useEffect, useMemo, useRef } from "react";

type PageViewport = { width: number; height: number };

const getTopmostRegion = (regions: SourceDocumentRegion[]) =>
  regions.reduce((topmost, region) => {
    if (
      region.page_number < topmost.page_number ||
      (region.page_number === topmost.page_number &&
        region.bbox.y0 < topmost.bbox.y0)
    ) {
      return region;
    }
    return topmost;
  });

const arePagesMeasurable = (
  targetPageNumber: number,
  pageViewports: Record<number, PageViewport>,
) => {
  for (let page = 1; page <= targetPageNumber; page++) {
    if (!pageViewports[page]) return false;
  }
  return true;
};

export const useScrollToBoundingBox = ({
  containerRef,
  pageViewports,
  regions,
}: {
  containerRef: RefObject<HTMLDivElement | null>;
  pageViewports: Record<number, PageViewport>;
  regions?: SourceDocumentRegion[];
}) => {
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const scrolledRegionsRef = useRef<SourceDocumentRegion[] | null>(null);

  const firstRegion = useMemo(() => {
    if (!regions?.length) return null;
    return getTopmostRegion(regions);
  }, [regions]);

  // Pages render in no guaranteed order; a page above the target rendering
  // later would shift the target and invalidate a position measured too early.
  const isFirstRegionMeasurable = useMemo(() => {
    if (!firstRegion) return false;
    return arePagesMeasurable(firstRegion.page_number, pageViewports);
  }, [firstRegion, pageViewports]);

  useEffect(() => {
    const container = containerRef.current;
    if (
      !regions ||
      !firstRegion ||
      !isFirstRegionMeasurable ||
      !container ||
      scrolledRegionsRef.current === regions
    ) {
      return;
    }
    const pageElement = pageRefs.current[firstRegion.page_number];
    const viewport = pageViewports[firstRegion.page_number];
    if (!pageElement || !viewport) return;

    const containerRect = container.getBoundingClientRect();
    const pageRect = pageElement.getBoundingClientRect();
    const regionHeight =
      (firstRegion.bbox.y1 - firstRegion.bbox.y0) * viewport.height;
    const regionTop =
      pageRect.top -
      containerRect.top +
      container.scrollTop +
      firstRegion.bbox.y0 * viewport.height;

    scrolledRegionsRef.current = regions;
    container.scrollTo({
      behavior: "smooth",
      top: Math.max(regionTop - (container.clientHeight - regionHeight) / 2, 0),
    });
  }, [regions, firstRegion, isFirstRegionMeasurable, pageViewports]);

  const setPageRef =
    (pageNumber: number) => (element: HTMLDivElement | null) => {
      pageRefs.current[pageNumber] = element;
    };

  return { setPageRef };
};
